import { assertNotEmpty, type Optional } from '@festina-life/core'
import { RawSelect } from '@festina-life/ui/forms'
import { useTranslate } from '@festina-life/ui/hooks'

import { useTemplateContext } from '../../context/templateContext'
import { useLazyLayerMetaModel } from '../../context/useLazyLayerMetaModel'
import type {
  SubTemplateFieldValue,
  SupportedEditableTemplateFieldValue,
} from '../../types/templateModel'
import { generateEmptyFieldValue } from '../../uiModels/createEmptyFieldHelpers'
import { isInherited } from '../../uiModels/templateOperations/types'
import { InheritedValueCopier } from '../copyInheritedValue/InheritedValueCopier'
import { parentFromSubTemplateFieldValue } from './helpers'
import { useSubTemplateParentCandidates } from './useSubTemplateParentCandidates'

type Props = {
  variant: SubTemplateFieldValue
}

export const SubTemplateParentSelector = ({ variant }: Props) => {
  const { templateVersion, fieldValueForPath } = useTemplateContext()
  const { subTemplatedField } = variant
  assertNotEmpty(subTemplatedField)
  assertNotEmpty(templateVersion)

  const parentCandidates = useSubTemplateParentCandidates(variant)
  const translate = useTranslate()
  const metaModel = useLazyLayerMetaModel(templateVersion?.layer?.layerId)

  const handleChangedParent = (nextParent: Optional<string>) => {
    const nextVariants = [...(subTemplatedField.value?.variants ?? [])]
    const indexOfCurrentVariant = nextVariants.indexOf(variant)
    const { parent, fields, ...variantFields } = variant

    /**
     * It's important to filter out fields that are coming from parent variants that belong to the same template version.
     * We therefore only update the template with fields that are not inherited.
     */
    const nonInheritedFields = fields.filter(
      (field) => !!fieldValueForPath(field.path, 'unflattened'),
    )
    nextVariants.splice(indexOfCurrentVariant, 1, {
      ...variantFields,
      fields: nonInheritedFields,
      ...(nextParent
        ? {
            parent: {
              value: nextParent,
              origin: `${variantFields.path}.parent`,
            },
          }
        : {}),
      origin: variantFields.path,
    } satisfies SubTemplateFieldValue)

    const fieldValue =
      subTemplatedField.value ??
      generateEmptyFieldValue({
        field: subTemplatedField,
        meta: metaModel,
      })

    ;(
      subTemplatedField.update as (
        nextValue: SupportedEditableTemplateFieldValue,
      ) => void
    )({
      ...fieldValue,
      availableVariantTypes:
        subTemplatedField.value?.availableVariantTypes ?? [],
      variants: nextVariants,
      origin: fieldValue.path,
    })
  }

  const currentParent = parentFromSubTemplateFieldValue(variant)
  const isInheritedVariant = isInherited(variant)

  return (
    <div className="group relative">
      <RawSelect
        className="max-w-md"
        options={parentCandidates}
        onChange={handleChangedParent}
        placeholder={translate('none')}
        value={currentParent}
        disabled={isInheritedVariant}
      />
      {isInheritedVariant && variant.origin && currentParent && (
        <InheritedValueCopier
          onCopy={() => handleChangedParent(currentParent)}
          fromOrigin={variant.origin}
          fieldPath={variant.path}
        />
      )}
    </div>
  )
}
